import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import usePrevious from 'react-utils/hooks/usePrevious';
import { View } from 'foundations-components/components/View';
import ActivityFeedWithProvider from 'framework-asset-preview-lib/internal/assetPreview/components/AsyncActivityFeedWithProvider';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { hasActivityFeed } from 'framework-asset-preview-lib/internal/activity/utils/activityConfig';
import AssetsSection from 'framework-asset-preview-lib/internal/associations/components/AssetsSection';
import useCrmAssetAssociations from 'framework-asset-preview-lib/internal/associations/hooks/useCrmAssetAssociations';
import PerformanceSection from 'framework-asset-preview-lib/internal/performance/components/PerformanceSection';
import { PERFORMANCE_ALLOW_LIST } from 'framework-asset-preview-lib/internal/performance/utils/performanceConfig';
import { WithErrorBoundary } from 'framework-asset-preview-lib/internal/shared/components/ErrorBoundary';
import ErrorMessage from 'framework-asset-preview-lib/internal/shared/components/ErrorMessage';
import LoadingOverlay from 'framework-asset-preview-lib/internal/shared/components/LoadingOverlay';
import { TAB } from 'framework-asset-preview-lib/internal/shared/constants/assetPreview';
import { SENTRY_KEYS } from 'framework-asset-preview-lib/internal/shared/constants/sentry';
import useAssetPreviewTracking from 'framework-asset-preview-lib/internal/shared/hooks/useAssetPreviewTracking';
import useCrmObject from 'framework-asset-preview-lib/internal/shared/hooks/useCrmObject';
import useError from 'framework-asset-preview-lib/internal/shared/hooks/useError';
import useIsLoading from 'framework-asset-preview-lib/internal/shared/hooks/useIsLoading';
import useNonCrmObject from 'framework-asset-preview-lib/internal/shared/hooks/useNonCrmObject';
import useProperties from 'framework-asset-preview-lib/internal/shared/hooks/useProperties';
import usePropertyGroups from 'framework-asset-preview-lib/internal/shared/hooks/usePropertyGroups';
import { getCustomTabId } from 'framework-asset-preview-lib/utils/tabs';
import useRevealSandboxUrl from '../hooks/useRevealSandboxUrl';
import { getCardPreview, getPopoverPreview, getSidebarPreview, getSidepanelPreview, getCarouselPreview, getModalPreview } from '../utils/getPreviewComponent';
import { jsx as _jsx } from "react/jsx-runtime";
const PreviewSectionTag = styled.section.withConfig({
  displayName: "AssetPreview__PreviewSectionTag"
})(["width:100%;height:100%;"]);
function PreviewSection({
  children,
  dataTestId,
  objectId,
  objectType
}) {
  return /*#__PURE__*/_jsx(PreviewSectionTag, {
    "data-test-id": dataTestId,
    "data-test-object-id": objectId,
    "data-test-object-type": objectType,
    children: children
  });
}
function getObjectType(error) {
  if (error.customError) {
    return error.customError.errorType;
  } else if (error.isCrmObjectError) {
    return SENTRY_KEYS.CRM_SEARCH_ERROR;
  } else if (error.isNonCrmObjectError) {
    return SENTRY_KEYS.NON_CRM_OBJECT_ERROR;
  } else if (error.isPropertiesError) {
    return SENTRY_KEYS.CRM_PROPERTIES_ERROR;
  } else {
    return SENTRY_KEYS.CRM_TYPE_DEFINITION_ERROR;
  }
}

/**
 * Holds the main logic to render an asset preview:
 *   - fetch from CRM
 *   - fetch object properties
 *   - render error/loading states
 *   - render the correct asset preview based on @objectType and @previewType
 *
 * This component **is not** meant to be used outside of this lib!
 */
export default function AssetPreview({
  applicationId,
  cardProperties,
  customTabs,
  defaultTab = TAB.DETAILS,
  objectId,
  objectType,
  onLoadCrmObject,
  originObjectType,
  previewType,
  selectProps,
  tabs,
  _fullWidthDisplay,
  onInteraction,
  hideTabs,
  campaignObjectId
}) {
  const crmObject = useCrmObject();
  const nonCrmObject = useNonCrmObject();
  const propertyGroups = usePropertyGroups();
  const properties = useProperties();
  const isLoading = useIsLoading();
  const error = useError();
  const {
    isUngatedFor
  } = useUserInfo();
  useRevealSandboxUrl();
  const previousTrackingData = usePrevious({
    objectId,
    objectType,
    originObjectType,
    previewType
  });
  const crmAssociations = useCrmAssetAssociations();
  const {
    trackAssetPreviewInteraction
  } = useAssetPreviewTracking();

  /**
   * Only object types in @ASSOCIATIONS_ALLOW_LIST will have access to the "Assets" tab
   */
  const hasAssociationsTab = useMemo(() => {
    const hasCrmAssociationConfig = crmAssociations.length > 0;
    const isPresentInTabs = !tabs || tabs && tabs.includes(TAB.ASSETS);
    return hasCrmAssociationConfig && isPresentInTabs;
  }, [crmAssociations.length, tabs]);
  const hasPerformanceTab = useMemo(() => {
    const hasAllowList = PERFORMANCE_ALLOW_LIST.includes(objectType);
    const isPresentInTabs = !tabs || tabs && tabs.includes(TAB.PERFORMANCE);
    return isPresentInTabs && (hasAllowList || !!campaignObjectId);
  }, [tabs, campaignObjectId, objectType]);
  const hasActivityTab = useMemo(() => {
    const isPresentInTabs = !tabs || tabs && tabs.includes(TAB.ACTIVITY);
    return hasActivityFeed(objectType, isUngatedFor) && isPresentInTabs;
  }, [isUngatedFor, objectType, tabs]);
  useEffect(() => {
    /**
     * Track usage for the first time
     */
    if (previousTrackingData === undefined) {
      trackAssetPreviewInteraction({
        action: 'open preview'
      });
      return;
    }
    const currentTrackingData = {
      objectId,
      objectType,
      originObjectType,
      previewType
    };

    /**
     * Track subsequent changes (when side panel stays open but any of the properties in @currentTrackingData changes)
     */
    if (JSON.stringify(previousTrackingData) !== JSON.stringify(currentTrackingData)) {
      trackAssetPreviewInteraction({
        action: 'open preview'
      });
    }
  }, [objectId, objectType, originObjectType, previewType, previousTrackingData, trackAssetPreviewInteraction]);
  useEffect(() => {
    if (!onLoadCrmObject || isLoading) {
      return;
    }
    if (!crmObject) {
      return;
    }
    onLoadCrmObject(crmObject);
  }, [crmObject, isLoading, onLoadCrmObject]);
  const trackTabClick = useMemo(() => {
    return {
      [TAB.DETAILS]: () => trackAssetPreviewInteraction({
        action: 'open tab details'
      }),
      [TAB.ASSETS]: () => trackAssetPreviewInteraction({
        action: 'open tab assets'
      }),
      [TAB.PERFORMANCE]: () => trackAssetPreviewInteraction({
        action: 'open tab performance'
      }),
      [TAB.ACTIVITY]: () => trackAssetPreviewInteraction({
        action: 'open tab activity'
      })
    };
  }, [trackAssetPreviewInteraction]);

  /**
   * Returns the correct asset preview component by @objectType and @previewType
   */
  const PreviewComponent = useMemo(() => {
    switch (previewType) {
      default:
      case 'sidepanel':
        return getSidepanelPreview(objectType);
      case 'sidebar':
        return getSidebarPreview(objectType);
      case 'popover':
        return getPopoverPreview(objectType);
      case 'card':
        return getCardPreview(objectType);
      case 'carousel':
        return getCarouselPreview();
      case 'modal':
        return getModalPreview(objectType);
    }
  }, [objectType, previewType]);
  const defaultSelected = useMemo(() => {
    const isCustomTab = customTabs && Object.keys(customTabs).includes(defaultTab);
    if (isCustomTab) {
      return getCustomTabId(defaultTab);
    }
    const customTabsMap = Object.keys(customTabs || {}).reduce((acc, key) => {
      acc[getCustomTabId(key)] = true;
      return acc;
    }, {});
    const tabMapping = Object.assign({
      [TAB.ASSETS]: hasAssociationsTab,
      [TAB.PERFORMANCE]: hasPerformanceTab,
      [TAB.ACTIVITY]: hasActivityTab
    }, customTabsMap);
    return tabMapping[defaultTab] ? defaultTab : TAB.DETAILS;
  }, [customTabs, defaultTab, hasActivityTab, hasAssociationsTab, hasPerformanceTab]);
  if (!PreviewComponent) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      errorType: "PREVIEW_COMPONENT_NOT_FOUND",
      objectId: objectId,
      objectType: objectType
    });
  }
  if (error.isError) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      errorType: getObjectType(error),
      objectId: objectId,
      objectType: objectType,
      customError: error.customError
    });
  }
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      previewType: previewType
    });
  }
  if (!crmObject) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      errorType: "CRM_OBJECT_NOT_FOUND",
      objectId: objectId,
      objectType: objectType
    });
  }
  const renderTabContent = tabId => {
    switch (tabId) {
      case TAB.DETAILS:
        return /*#__PURE__*/_jsx(PreviewSection, {
          dataTestId: "framework-asset-preview-details-tab",
          objectId: objectId,
          objectType: objectType,
          children: /*#__PURE__*/_jsx(PreviewComponent, {
            crmObject: crmObject,
            cardProperties: cardProperties,
            nonCrmObject: nonCrmObject,
            onInteraction: onInteraction,
            originObjectType: originObjectType,
            properties: properties,
            propertyGroups: propertyGroups,
            selectProps: selectProps,
            _fullWidthDisplay: _fullWidthDisplay
          })
        });
      case TAB.ASSETS:
        if (!hasAssociationsTab) return null;
        return /*#__PURE__*/_jsx(PreviewSection, {
          dataTestId: "framework-asset-preview-assets-tab",
          objectId: objectId,
          objectType: objectType,
          children: /*#__PURE__*/_jsx(AssetsSection, {
            crmObject: crmObject,
            nonCrmObject: nonCrmObject
          })
        });
      case TAB.PERFORMANCE:
        if (!hasPerformanceTab) return null;
        return /*#__PURE__*/_jsx(PreviewSection, {
          dataTestId: "framework-asset-preview-performance-tab",
          objectId: objectId,
          objectType: objectType,
          children: /*#__PURE__*/_jsx(PerformanceSection, {
            crmObject: crmObject,
            nonCrmObject: nonCrmObject,
            onInteraction: onInteraction,
            properties: properties,
            propertyGroups: propertyGroups,
            campaignObjectId: campaignObjectId
          })
        });
      case TAB.ACTIVITY:
        if (!hasActivityTab) return null;
        return /*#__PURE__*/_jsx(PreviewSection, {
          dataTestId: "framework-asset-preview-activity-tab",
          objectId: objectId,
          objectType: objectType,
          children: /*#__PURE__*/_jsx(View, {
            marginBlockStart: "250",
            paddingBlock: "0",
            paddingInline: "500",
            children: /*#__PURE__*/_jsx(ActivityFeedWithProvider, {
              applicationId: `framework-asset-preview-activity-tab-${objectType}`,
              objectId: typeof objectId === 'string' ? parseInt(objectId, 10) : objectId,
              objectTypeId: objectType,
              horizontalSize: 20
            })
          })
        });
      default:
        if (customTabs && tabId.startsWith('custom-tab-')) {
          const customTabKey = tabId.replace('custom-tab-', '');
          const customTab = customTabs[customTabKey];
          if (customTab) {
            return /*#__PURE__*/_jsx(PreviewSection, {
              dataTestId: `framework-asset-preview-${tabId}-tab`,
              objectId: objectId,
              objectType: objectType,
              children: /*#__PURE__*/_jsx(WithErrorBoundary, {
                applicationId: applicationId,
                fingerprint: SENTRY_KEYS.CUSTOM_TAB,
                children: () => customTab.renderTabContent({
                  crmObject,
                  nonCrmObject,
                  properties,
                  propertyGroups
                })
              })
            });
          }
        }
        return null;
    }
  };
  const assetPreviewSection = renderTabContent(TAB.DETAILS);
  if (hideTabs) {
    return renderTabContent(defaultSelected) || assetPreviewSection;
  }
  if ((previewType === 'sidepanel' || previewType === 'sidebar') && (hasAssociationsTab || hasPerformanceTab || hasActivityTab)) {
    return /*#__PURE__*/_jsx(View, {
      maxInlineSize: "100%",
      children: /*#__PURE__*/_jsx(Tabs, {
        defaultSelected: defaultSelected,
        panelStyles: {
          padding: '0'
        },
        children: [/*#__PURE__*/_jsx(Tab, {
          tabId: TAB.DETAILS,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkAssetPreviewLib.tabs.details.title"
          }),
          "data-test-id": "framework-asset-preview-details-tab-button",
          "data-coaching-tip": `preview-lib-${objectType}-${objectId}-tab-${TAB.DETAILS}`,
          onClick: trackTabClick[TAB.DETAILS],
          children: renderTabContent(TAB.DETAILS)
        }, TAB.DETAILS), hasAssociationsTab && /*#__PURE__*/_jsx(Tab, {
          tabId: TAB.ASSETS,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkAssetPreviewLib.tabs.assets.title"
          }),
          "data-test-id": "framework-asset-preview-assets-tab-button",
          "data-coaching-tip": `preview-lib-${objectType}-${objectId}-tab-${TAB.ASSETS}`,
          onClick: trackTabClick[TAB.ASSETS],
          children: renderTabContent(TAB.ASSETS)
        }, TAB.ASSETS), hasPerformanceTab && /*#__PURE__*/_jsx(Tab, {
          tabId: TAB.PERFORMANCE,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkAssetPreviewLib.tabs.performance.title"
          }),
          "data-test-id": "framework-asset-preview-performance-tab-button",
          "data-coaching-tip": `preview-lib-${objectType}-${objectId}-tab-${TAB.PERFORMANCE}`,
          onClick: trackTabClick[TAB.PERFORMANCE],
          children: renderTabContent(TAB.PERFORMANCE)
        }, TAB.PERFORMANCE), hasActivityTab && /*#__PURE__*/_jsx(Tab, {
          tabId: TAB.ACTIVITY,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkAssetPreviewLib.tabs.activity.title"
          }),
          "data-test-id": "framework-asset-preview-activity-tab-button",
          "data-coaching-tip": `preview-lib-${objectType}-${objectId}-tab-${TAB.ACTIVITY}`,
          onClick: trackTabClick[TAB.ACTIVITY],
          children: renderTabContent(TAB.ACTIVITY)
        }, TAB.ACTIVITY), customTabs && Object.entries(customTabs).map(([key, customTab]) => {
          const namespacedKey = getCustomTabId(key);
          return /*#__PURE__*/_jsx(Tab, {
            tabId: namespacedKey,
            title: customTab.title,
            "data-test-id": `framework-asset-preview-${namespacedKey}-tab-button`,
            "data-coaching-tip": `preview-lib-${objectType}-${objectId}-tab-${namespacedKey}`,
            children: renderTabContent(namespacedKey)
          }, namespacedKey);
        })
        // Trellis' Tabs requires its children to be a flat array
        ].flat()
      })
    });
  }
  if (previewType === 'carousel') {
    return /*#__PURE__*/_jsx(PreviewComponent, {
      crmObject: crmObject,
      cardProperties: cardProperties,
      nonCrmObject: nonCrmObject,
      onInteraction: onInteraction,
      originObjectType: originObjectType,
      properties: properties,
      propertyGroups: propertyGroups,
      selectProps: selectProps,
      _fullWidthDisplay: _fullWidthDisplay
    });
  }
  return assetPreviewSection;
}